import i18next from 'i18next';
import permissions from '@shared/config/permissions';
import { INFO_PANE_PAGES } from 'redux_store';
// TODO(Phase 7): CUSTOM_TAGS_OPTIONS / FILTER_TYPES still live in
// the legacy table infrastructure. They move to shared when the
// table widgets migrate. Until then this entity is exempt from the
// no-restricted-imports boundary via .eslintrc (same as resource).
import {
  CUSTOM_TAGS_OPTIONS,
  PATIENT_CUSTOM_TAGS_OPTIONS,
  SURVEY_TAGS_OPTIONS,
} from '_legacy/components/Shared/CustomTags';
import FILTER_TYPES from '@shared/ui/Table/Filters/constants';
import { ASSESSMENT_STATUSES_FILTER_OPTIONS_WITH_NO_SHOW } from '@entities/assessment';
import { BOT_UPLOADS_STATUSES_OPTIONS, DOCUMENT_TYPES_OPTIONS } from './constants';

/**
 * Column definition factory for the bot-activities table.
 *
 * Accepts `isPatientPage` because the bot-activities table is reused on
 * the patient dashboard where some columns (first / last name, DOB) are
 * redundant and should be hidden.
 *
 * The shape follows the Mantine-React-Table schema extended with the
 * project's own table infrastructure (filter config, cell config,
 * permission gating, isDefaultCell toggle).
 */
export const GET_BOT_ACTIVITIES_COLUMNS = isPatientPage => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: { filterType: FILTER_TYPES.SELECT, options: CUSTOM_TAGS_OPTIONS },
    cell: { withBubbles: true },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 200,
    header: i18next.t('tables:surveyTags'),
    accessorKey: 'surveyTags',
    filter: { filterType: FILTER_TYPES.SELECT, options: SURVEY_TAGS_OPTIONS },
    cell: { withBubbles: true },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: { filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 260,
    header: i18next.t('tables:patientCustomTags'),
    accessorKey: 'patientCustomTags',
    filter: { filterType: FILTER_TYPES.SELECT, options: PATIENT_CUSTOM_TAGS_OPTIONS },
    cell: { withBubbles: true },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 180,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: { infoPanePage: INFO_PANE_PAGES.patient_page },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: { infoPanePage: INFO_PANE_PAGES.patient_page },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: { infoPanePage: INFO_PANE_PAGES.patient_page },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    hidden: isPatientPage,
    filter: { filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:assessmentName'),
    accessorKey: 'surveyName',
    filter: { filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'orderDate',
    filter: { shouldRange: true, filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:appointmentTimestamp'),
    accessorKey: 'appointmentTimestamp',
    filter: { shouldRange: true, hasNoMaxDate: true, filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:invitedTimestamp'),
    accessorKey: 'invitedTimestamp',
    filter: { shouldRange: true, hasNoMaxDate: true, filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:createdBy'),
    accessorKey: 'createdBy',
    filter: { filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:status'),
    accessorKey: 'status',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: ASSESSMENT_STATUSES_FILTER_OPTIONS_WITH_NO_SHOW,
      // The options array is lifecycle-ordered (see statuses.js) —
      // skip SelectFilter's generic badge-tone sorting.
      preserveOptionOrder: true,
    },
    cell: { withBubble: true },
  },
  // ── Per-document columns ────────────────────────────────────────
  // The API returns one row per document, so these describe THIS row's
  // document, not the assessment: `BotActivityQuery.RowKeys` on the
  // server. The three E-Visit columns this block replaces are gone —
  // an E-Visit is now simply a row with `documentType: programReport`.
  //
  // None of them may be filtered negatively: the API answers 422 to a
  // negated term on a row key. The plain SELECT and text filters have
  // no exclude mode to begin with; the one that does is flagged below.
  {
    size: 200,
    header: i18next.t('tables:providerAssignment'),
    accessorKey: 'providerAssignment',
    filter: {},
    cell: {},
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:documentType'),
    accessorKey: 'documentType',
    filter: { filterType: FILTER_TYPES.SELECT, options: DOCUMENT_TYPES_OPTIONS },
    cell: {},
  },
  {
    size: 200,
    header: i18next.t('tables:botIntegration'),
    accessorKey: 'botIntegration',
    // Served by `/lookups/botActivities?fields=botIntegration`, which
    // lists the bots that actually appear on the rows — so a new
    // integration shows up without a dashboard release. It is the only
    // row key the lookups endpoint serves (`IsRowLookupField`).
    filter: { filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT, disableExcludeMode: true },
    cell: {},
  },
  {
    size: 200,
    header: i18next.t('tables:documentStatus'),
    accessorKey: 'documentStatus',
    filter: { filterType: FILTER_TYPES.SELECT, options: BOT_UPLOADS_STATUSES_OPTIONS },
    cell: {},
  },
  {
    size: 200,
    header: i18next.t('tables:uploadTimestamp'),
    accessorKey: 'uploadTimestamp',
    filter: { shouldRange: true, filterType: FILTER_TYPES.DATE },
    cell: {},
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:failureReason'),
    accessorKey: 'failureReason',
    filter: {},
    cell: {},
    isDefaultCell: true,
  },
];
